import React, { useEffect, useRef } from 'react';

// Sfondo "rete" discreto. Nodi e fili restano solo nello spazio vuoto:
// le zone di rispetto sono calcolate dai contenuti.
// - auto=false: solo i blocchi marcati con data-net-exclude (comportamento storico)
// - auto=true: tutti i contenitori e testi comuni, con lo stesso margine di 24px
const NODE = '61, 219, 169'; // #3ddba9
const MARGIN = 24;           // zona di rispetto attorno ai contenuti
const CELL = 12;             // risoluzione della mappa delle zone vietate
const PULL = 0.45;
const EASE = 0.1;
const MAX_LINK_ALPHA = 0.22;
const MAX_CURSOR_ALPHA = 0.35;
const DOT_ALPHA = 0.3;
const REMEASURE_MS = 200;

const MANUAL_SELECTOR = '[data-net-exclude]';
// contenitori e contenuti comuni: pannelli, tabelle, tab, header, liste, campi, testi
const AUTO_SELECTOR = [
  '[data-net-exclude]',
  '.ht-panel', '.ht-card', '.ht-strip', '.ht-row',
  'header', 'table', 'form', 'fieldset',
  '[role="tablist"]', '[role="tab"]', '[role="dialog"]', '[role="menu"]',
  'button', 'a', 'input', 'select', 'textarea', 'label', 'img', 'svg',
  'h1', 'h2', 'h3', 'h4', 'h5', 'p', 'li', 'td', 'th',
].join(',');

const MODAL_SELECTOR = '[role="dialog"], div[class*="fixed"][class*="inset-0"]';

export const AmbientNetwork = ({ auto = false }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const selector = auto ? AUTO_SELECTOR : MANUAL_SELECTOR;

    let w = 0, h = 0, dpr = 1, nodes = [], raf = 0, linkDist = 150, attractR = 170;
    let isPhone = false, hidden = false;
    let grid = null, cols = 0, rows = 0, busyRatio = 0, punch = [];
    const p = { x: 0, y: 0, active: false };

    const blocked = (x, y) => {
      if (!grid) return false;
      const cx = (x / CELL) | 0;
      const cy = (y / CELL) | 0;
      if (cx < 0 || cy < 0 || cx >= cols || cy >= rows) return false;
      return grid[cy * cols + cx] === 1;
    };

    // test approssimato segmento/zona: campiona il segmento sulla mappa
    const crosses = (x1, y1, x2, y2) => {
      if (blocked(x1, y1) || blocked(x2, y2)) return true;
      for (let t = 1; t < 8; t++) {
        const k = t / 8;
        if (blocked(x1 + (x2 - x1) * k, y1 + (y2 - y1) * k)) return true;
      }
      return false;
    };

    const isModalOpen = () => {
      const found = document.querySelectorAll(MODAL_SELECTOR);
      for (const el of found) {
        if (el === canvas) continue;
        const r = el.getBoundingClientRect();
        if (r.width > w * 0.5 && r.height > h * 0.5) return true;
      }
      return false;
    };

    // mappa delle zone vietate (una cella = CELL px), ricalcolata solo su evento
    const measure = () => {
      cols = Math.max(1, Math.ceil(w / CELL));
      rows = Math.max(1, Math.ceil(h / CELL));
      grid = new Uint8Array(cols * rows);
      punch = [];
      let busy = 0;

      const els = document.querySelectorAll(selector);
      for (const el of els) {
        const r = el.getBoundingClientRect();
        if (r.width <= 0 || r.height <= 0) continue;
        if (r.bottom < -MARGIN || r.top > h + MARGIN) continue;
        if (r.right < -MARGIN || r.left > w + MARGIN) continue;
        // un contenitore che copre quasi tutto non è una zona utile
        if (r.width >= w && r.height >= h) continue;

        const x0 = Math.max(0, Math.floor((r.left - MARGIN) / CELL));
        const x1 = Math.min(cols - 1, Math.floor((r.right + MARGIN) / CELL));
        const y0 = Math.max(0, Math.floor((r.top - MARGIN) / CELL));
        const y1 = Math.min(rows - 1, Math.floor((r.bottom + MARGIN) / CELL));
        for (let cy = y0; cy <= y1; cy++) {
          const base = cy * cols;
          for (let cx = x0; cx <= x1; cx++) {
            if (grid[base + cx] === 0) { grid[base + cx] = 1; busy++; }
          }
        }
        if (r.width * r.height > 600 && punch.length < 140) {
          punch.push({ x: r.left - MARGIN, y: r.top - MARGIN, w: r.width + MARGIN * 2, h: r.height + MARGIN * 2 });
        }
      }
      busyRatio = busy / (cols * rows);
    };

    const freeSpot = () => {
      for (let i = 0; i < 40; i++) {
        const x = Math.random() * w;
        const y = Math.random() * h;
        if (!blocked(x, y)) return { x, y };
      }
      return { x: Math.random() * w, y: h - Math.random() * Math.max(40, h * 0.2) };
    };

    // pagine dense: meno nodi, in proporzione allo spazio occupato
    const nodeCount = () => {
      const base = isPhone ? 20 : 40;
      const floor = isPhone ? 8 : 14;
      return Math.max(floor, Math.round(base * (1 - busyRatio)));
    };

    const seed = () => {
      nodes = Array.from({ length: nodeCount() }, () => {
        const { x, y } = freeSpot();
        return {
          ox: x, oy: y, x, y,
          vx: (Math.random() - 0.5) * 0.2,
          vy: (Math.random() - 0.5) * 0.2,
          r: 1 + Math.random() * 1.2,
        };
      });
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.round(window.innerWidth);
      h = Math.round(window.innerHeight);
      isPhone = w < 640;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const diag = Math.hypot(w, h);
      linkDist = Math.max(140, diag * 0.1);
      attractR = Math.max(160, diag * 0.12);
      measure();
      seed();
      draw();
    };

    const punchHoles = () => {
      if (!punch.length) return;
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = '#000';
      for (const r of punch) ctx.fillRect(r.x, r.y, r.w, r.h);
      ctx.restore();
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      if (hidden) return;
      ctx.lineWidth = 1;

      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > linkDist * linkDist) continue;
          if (crosses(a.x, a.y, b.x, b.y)) continue;
          const alpha = (1 - Math.sqrt(d2) / linkDist) * MAX_LINK_ALPHA;
          ctx.strokeStyle = `rgba(${NODE}, ${alpha.toFixed(3)})`;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      if (p.active) {
        for (const n of nodes) {
          const d = Math.hypot(n.x - p.x, n.y - p.y);
          if (d > linkDist) continue;
          if (crosses(p.x, p.y, n.x, n.y)) continue;
          const alpha = (1 - d / linkDist) * MAX_CURSOR_ALPHA;
          ctx.strokeStyle = `rgba(${NODE}, ${alpha.toFixed(3)})`;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(n.x, n.y);
          ctx.stroke();
        }
      }

      ctx.fillStyle = `rgba(${NODE}, ${DOT_ALPHA})`;
      for (const n of nodes) {
        if (blocked(n.x, n.y)) continue;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }

      punchHoles();
    };

    const advance = () => {
      for (const n of nodes) {
        n.ox += n.vx;
        n.oy += n.vy;
        if (n.ox < 0 || n.ox > w) n.vx *= -1;
        if (n.oy < 0 || n.oy > h) n.vy *= -1;
        let tx = n.ox, ty = n.oy;
        if (p.active) {
          const dx = p.x - n.ox, dy = p.y - n.oy;
          if (Math.hypot(dx, dy) < attractR) {
            tx = n.ox + dx * PULL;
            ty = n.oy + dy * PULL;
          }
        }
        if (blocked(tx, ty)) { tx = n.x; ty = n.y; }
        n.x += (tx - n.x) * EASE;
        n.y += (ty - n.y) * EASE;
      }
      draw();
    };

    const step = () => {
      advance();
      raf = requestAnimationFrame(step);
    };

    // su telefono e con prefers-reduced-motion: fotogramma fisso
    const animated = !reduced && !isPhone;
    const start = () => { if (!animated || raf || hidden || document.hidden) return; raf = requestAnimationFrame(step); };
    const stop = () => { if (raf) cancelAnimationFrame(raf); raf = 0; };

    const setPointer = (clientX, clientY) => {
      p.x = clientX;
      p.y = clientY;
      p.active = !blocked(p.x, p.y);
      if (!animated) draw();
    };

    const onPointerMove = (e) => setPointer(e.clientX, e.clientY);
    const onTouchMove = (e) => {
      const t = e.touches[0];
      if (t) setPointer(t.clientX, t.clientY);
    };
    const clearPointer = () => { p.active = false; if (!animated) draw(); };
    const onVisibility = () => (document.hidden ? stop() : start());

    // ricalcolo limitato nel tempo: cambio pagina, contenuto, scroll
    let timer = 0;
    const scheduleRemeasure = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = 0;
        const modal = isModalOpen();
        if (modal !== hidden) {
          hidden = modal;
          canvas.style.opacity = hidden ? '0' : '1';
          if (hidden) stop();
        }
        measure();
        if (nodes.length !== nodeCount()) seed();
        if (hidden || !animated) draw();
        if (!hidden) start();
      }, REMEASURE_MS);
    };

    resize();
    start();

    const mo = new MutationObserver(scheduleRemeasure);
    mo.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('resize', resize);
    window.addEventListener('scroll', scheduleRemeasure, { passive: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('touchstart', onTouchMove, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', clearPointer, { passive: true });
    document.addEventListener('mouseleave', clearPointer);
    window.addEventListener('blur', clearPointer);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      if (timer) clearTimeout(timer);
      mo.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', scheduleRemeasure);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('touchstart', onTouchMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', clearPointer);
      document.removeEventListener('mouseleave', clearPointer);
      window.removeEventListener('blur', clearPointer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [auto]);

  return (
    <canvas
      ref={canvasRef}
      className="ambient-network"
      aria-hidden="true"
      data-testid="ambient-network-canvas"
    />
  );
};

export default AmbientNetwork;
